import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../lib/auth";
import { ACCION_TIPO_LABEL, DIRECCION_LABEL } from "../lib/constants";
import { Plus, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import AccionForm from "../components/AccionForm";

export default function Acciones() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [acciones, setAcciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = () => {
    api.get("/acciones").then((r) => setAcciones(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const onCreated = () => {
    setShowForm(false);
    load();
    toast.success("Acción registrada");
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar acción?")) return;
    try {
      await api.delete(`/acciones/${id}`);
      load();
    } catch {
      toast.error("Error");
    }
  };

  const canRegister = user?.role !== "directivo";

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-10">
          <div>
            <div className="section-marker">{t("acciones.kicker")}</div>
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter mt-2">
              {t("acciones.page_title")}<span className="serif-em text-zinc-900">.</span>
            </h1>
          </div>
          {canRegister && (
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Link to="/acciones/bulk" className="border border-zinc-300 px-5 py-3 inline-flex items-center justify-center gap-2 hover:bg-zinc-50 font-medium" data-testid="btn-bulk-upload">
                <Upload size={18} strokeWidth={1.5} /> {t("acciones.btn_bulk")}
              </Link>
              <button onClick={() => setShowForm(true)} className="bg-zinc-950 text-white px-5 py-3 inline-flex items-center justify-center gap-2 hover:bg-zinc-800 font-medium" data-testid="btn-new-accion">
                <Plus size={18} strokeWidth={1.5} /> {t("acciones.btn_new")}
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 sm:mb-10 pb-8 sm:pb-10 border-b border-zinc-200">
          <div>
            <div className="section-marker">{t("acciones.kpi_total")}</div>
            <div className="kpi-number text-5xl sm:text-6xl mt-2">{acciones.length}</div>
          </div>
          <div>
            <div className="section-marker">{t("acciones.kpi_participantes")}</div>
            <div className="kpi-number text-5xl sm:text-6xl mt-2">{acciones.reduce((s, a) => s + (a.num_participantes || 0), 0)}</div>
          </div>
          <div>
            <div className="section-marker">{t("acciones.kpi_convenios_activados")}</div>
            <div className="kpi-number text-5xl sm:text-6xl mt-2">{new Set(acciones.map(a => a.convenio_id)).size}</div>
          </div>
        </div>

        {loading ? (
          <div className="text-zinc-500">{t("common.loading")}</div>
        ) : (
          <div className="border-t-2 border-zinc-950 overflow-x-auto">
            <table className="w-full min-w-[760px]" data-testid="acciones-table">
              <thead>
                <tr>
                  <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">Tipo</th>
                  <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">Convenio</th>
                  <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">Facultad</th>
                  <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">Período</th>
                  <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-zinc-500 text-right">Participantes</th>
                  <th className="py-3"></th>
                </tr>
              </thead>
              <tbody>
                {acciones.length === 0 && (
                  <tr><td colSpan={6} className="py-16 text-center text-zinc-500">Sin acciones registradas</td></tr>
                )}
                {acciones.map((a) => (
                  <tr key={a.id} className="border-t border-zinc-100 row-hover" data-testid={`accion-row-${a.id}`}>
                    <td className="py-4">
                      <div className="font-medium">{ACCION_TIPO_LABEL[a.tipo]}</div>
                      {a.direccion !== "n_a" && <div className="text-xs text-zinc-500">{DIRECCION_LABEL[a.direccion]}</div>}
                    </td>
                    <td className="py-4">
                      <Link to={`/convenios/${a.convenio_id}`} className="hover:underline">
                        {a.convenio_snapshot?.institucion_nombre || "—"}
                      </Link>
                      {a.convenio_snapshot?.institucion_pais && (
                        <div className="text-xs text-zinc-500">{a.convenio_snapshot.institucion_pais}</div>
                      )}
                    </td>
                    <td className="py-4 text-zinc-600">{a.facultad_nombre || "—"}</td>
                    <td className="py-4 text-zinc-600 tabular-nums">{a.semestre}/{a.anio}</td>
                    <td className="py-4 text-right tabular-nums font-medium">{a.num_participantes}</td>
                    <td className="py-4 text-right">
                      {canRegister && (
                        <button onClick={() => remove(a.id)} className="text-zinc-400 hover:text-red-700" data-testid={`del-${a.id}`}>
                          <Trash2 size={14} strokeWidth={1.5} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showForm && <AccionForm onClose={() => setShowForm(false)} onCreated={onCreated} />}
      </div>
    </Layout>
  );
}
